"use client";

import { Weight } from "@/components/icons";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

export interface DimensionGridProps {
  length: string;
  width: string;
  height: string;
  weight: string;
  palletized: boolean;
  palletHeight: string;
  specificQtyEnabled: boolean;
  targetQty: string;
  showPalletized: boolean;
  onLengthChange: (value: string) => void;
  onWidthChange: (value: string) => void;
  onHeightChange: (value: string) => void;
  onWeightChange: (value: string) => void;
  onPalletizedChange: (value: boolean) => void;
  onPalletHeightChange: (value: string) => void;
  onSpecificQtyEnabledChange: (value: boolean) => void;
  onTargetQtyChange: (value: string) => void;
}

function InchesField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        min="0"
        step="1"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1"
        variant="mono"
      />
    </div>
  );
}

function WeightField({
  weight,
  onWeightChange,
}: Pick<DimensionGridProps, "weight" | "onWeightChange">) {
  return (
    <div>
      <Label htmlFor="item-weight">
        Weight (lbs)
        <span className="ml-1 text-muted-foreground font-normal">
          optional
        </span>
      </Label>
      <div className="mt-1 flex items-center gap-2">
        <Input
          id="item-weight"
          type="number"
          min="0"
          step="1"
          value={weight}
          onChange={(event) => onWeightChange(event.target.value)}
          variant="mono"
          placeholder="0"
        />
        <Weight className="h-4 w-4 text-muted-foreground shrink-0" />
      </div>
    </div>
  );
}

function PalletizedOption({
  palletized,
  palletHeight,
  onPalletizedChange,
  onPalletHeightChange,
}: Pick<
  DimensionGridProps,
  "palletized" | "palletHeight" | "onPalletizedChange" | "onPalletHeightChange"
>) {
  return (
    <div className="flex items-center gap-2">
      <Checkbox
        id="item-palletized"
        checked={palletized}
        onCheckedChange={(checked) => onPalletizedChange(checked === true)}
      />
      <Label htmlFor="item-palletized" weight="normal">
        Palletized
      </Label>
      {palletized && (
        <div className="flex items-center gap-1.5">
          <Input
            id="item-pallet-height"
            aria-label="Pallet height (in)"
            type="number"
            min="0"
            step="0.5"
            value={palletHeight}
            onChange={(event) => onPalletHeightChange(event.target.value)}
            className="w-16"
            variant="mono"
            size="compact"
          />
          <span className="text-xs text-muted-foreground">in/pallet</span>
        </div>
      )}
    </div>
  );
}

function SpecificQuantityOption({
  specificQtyEnabled,
  targetQty,
  onSpecificQtyEnabledChange,
  onTargetQtyChange,
}: Pick<
  DimensionGridProps,
  | "specificQtyEnabled"
  | "targetQty"
  | "onSpecificQtyEnabledChange"
  | "onTargetQtyChange"
>) {
  return (
    <div className="flex items-center gap-2">
      <Checkbox
        id="item-specific-qty"
        checked={specificQtyEnabled}
        onCheckedChange={(checked) => onSpecificQtyEnabledChange(checked === true)}
      />
      <Label htmlFor="item-specific-qty" weight="normal">
        Specific quantity
      </Label>
      {specificQtyEnabled && (
        <Input
          id="item-target-qty"
          aria-label="Target quantity"
          type="number"
          min="1"
          step="1"
          value={targetQty}
          onChange={(event) => onTargetQtyChange(event.target.value)}
          placeholder="e.g. 300"
          className="w-24"
          variant="mono"
          size="compact"
        />
      )}
    </div>
  );
}

export function DimensionGrid(props: DimensionGridProps) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <InchesField id="item-length" label="Length (in)" value={props.length} onChange={props.onLengthChange} />
      <InchesField id="item-width" label="Width (in)" value={props.width} onChange={props.onWidthChange} />
      <InchesField id="item-height" label="Height (in)" value={props.height} onChange={props.onHeightChange} />
      <WeightField weight={props.weight} onWeightChange={props.onWeightChange} />
      <div className="col-span-2 flex flex-wrap items-center gap-x-4 gap-y-2">
        {props.showPalletized && (
          <PalletizedOption
            palletized={props.palletized}
            palletHeight={props.palletHeight}
            onPalletizedChange={props.onPalletizedChange}
            onPalletHeightChange={props.onPalletHeightChange}
          />
        )}
        <SpecificQuantityOption
          specificQtyEnabled={props.specificQtyEnabled}
          targetQty={props.targetQty}
          onSpecificQtyEnabledChange={props.onSpecificQtyEnabledChange}
          onTargetQtyChange={props.onTargetQtyChange}
        />
      </div>
    </div>
  );
}
